import { variableSearch } from '@app/const';
import {
  defaultTtsVoice,
  Design,
  DesignModuleNode,
  DesignObject,
  LanguageCode,
  PromptObjectInput,
  TextToSpeechVoiceOrDefault,
  WorkflowPrompt,
  WorkflowPromptTextForLanguage
} from '@app/types';
import { getPromptVoiceChannel } from '../prompt';

export function getWfsPrompt(
  serialized: Design,
  nodeOrObject: DesignModuleNode | DesignObject
): WorkflowPrompt[] {
  const {
    components: { objects },
    languages
  } = serialized;
  let { name, props } = nodeOrObject;
  const object = ('wftName' in nodeOrObject) ? nodeOrObject : (
    objects.find((obj) => {
      return obj.id === nodeOrObject.props.parentComponentID;
    })
  );
  if (object?.wftName) {
    name = object.wftName;
  }
  let {
    audioUrls,
    inputs: rawInputs = []
  } = props.config;
  const getLockedUrl = (
    languageCode: LanguageCode,
    voiceCode: TextToSpeechVoiceOrDefault
  ): string | undefined => {
    return audioUrls?.[languageCode]?.[voiceCode]?.lockedUrl;
  };

  const inputs = (rawInputs as PromptObjectInput[]).map(({ argumentName, defaultValue, speakAs }) => {
    return {
      name: argumentName,
      defaultValue,
      domain: speakAs
    };
  });
  const textObject = getPromptVoiceChannel({ promptNodeConfig: props.config, serialized });

  const prompt: WorkflowPrompt = {
    name,
    inputs,
    text: {},
    readyForActivation: true
  };
  // Fallback prompts have their text split up by variables just in case TTP fails.
  const fallbackPrompts: WorkflowPrompt[] = [];

  languages.map(({ language }) => {
    let languageTextObject: WorkflowPromptTextForLanguage | null = textObject?.[language] ?? null;
    let languageString = '';

    if (typeof languageTextObject === 'string') {
      languageTextObject = languageTextObject.replace(/\s+/g, ' ').trim();
      languageString = languageTextObject;
      if (languageString) {
        const lockedVoiceEntries: Exclude<WorkflowPromptTextForLanguage, string> = {};
        Object.entries(audioUrls?.[language] ?? {}).forEach(([voiceCode]) => {
          const lockedUrl = getLockedUrl(language, voiceCode as TextToSpeechVoiceOrDefault);
          if (voiceCode !== defaultTtsVoice && isAbsoluteUrl(lockedUrl)) {
            lockedVoiceEntries[voiceCode] = {
              audioUri: lockedUrl,
              text: languageString
            };
          }
        });
        if (Object.keys(lockedVoiceEntries).length > 0) {
          const languageTextMap: Exclude<WorkflowPromptTextForLanguage, string> = {
            ...lockedVoiceEntries
          };
          languageTextMap[defaultTtsVoice] = { text: languageString };
          languageTextObject = languageTextMap;
        }
      }
    } else if (languageTextObject) {
      const mappedLanguageTextObject: Exclude<WorkflowPromptTextForLanguage, string> = {};
      Object.entries(languageTextObject).forEach(([voiceCode, textObject]) => {
        const text = textObject.text.replace(/\s+/g, ' ').trim();
        if (!text) {
          return;
        }
        const lockedUrl = getLockedUrl(language, voiceCode as TextToSpeechVoiceOrDefault);
        mappedLanguageTextObject[voiceCode] = {
          ...(voiceCode !== defaultTtsVoice && isAbsoluteUrl(lockedUrl) ? { audioUri: lockedUrl } : {}),
          text
        };
      });
      languageTextObject = Object.keys(mappedLanguageTextObject).length > 0 ? mappedLanguageTextObject : null;
      languageString = mappedLanguageTextObject[defaultTtsVoice]?.text ?? Object.values(mappedLanguageTextObject)[0]?.text ?? '';
    }

    if (!languageTextObject || !languageString) {
      return;
    }

    prompt.text[language] = languageTextObject;
    const splitText = languageString.split(variableSearch);
    if (splitText.length < 2) {
      return;
    }

    splitText.filter((textSegment, index) => {
      return (
        !!textSegment.trim()
        // `* 4` to skip capture groups.
        && index % 4 === 0
      );
    }).forEach((textSegment, index) => {
      if (!fallbackPrompts[index]) {
        fallbackPrompts.push({
          name: `${name}_TTP_FALLBACK_${index + 1}`,
          inputs: [],
          text: {},
          readyForActivation: true
        });
      }
      fallbackPrompts[index].text[language] = textSegment;
    });
  });

  return [
    prompt,
    ...fallbackPrompts
  ];
}

function isAbsoluteUrl(url?: string): boolean {
  if (!url) {
    return false;
  }
  try {
    return new URL(url).protocol.length > 0;
  } catch (_error) {
    return false;
  }
}
